import { type ReactNode, type RefObject } from 'react'

import FloatingAssistantButton from '@/components/ai-assistant-button/FloatingAssistantButton'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useLeaveDraftOnSmallScreen } from '../hooks/useLeaveDraftOnSmallScreen'
import { selectChatPanelOpen } from '../store/selectors'
import { ChatPanelTabType, DraftWorkspaceActions } from '../store/slice'

type FloatingAiAssistantButtonProps = {
  draftArtifactVersionId: string
  isSmallScreen: boolean
  /* Focused when the panel is collapsed, since the button that collapsed it unmounts. */
  reopenButtonRef?: RefObject<HTMLButtonElement>
  /* Rendered to the right of the button. */
  trailingContent?: ReactNode
}

/**
 * Floating button that returns to the conversation: it opens the collapsed chat panel, or on small screens closes the draft.
 *
 * It floats over the document's bottom-right corner, so draft-panel.css reserves matching room below the document for the
 * last lines to scroll clear of it. Resizing or repositioning anything here means revisiting that padding.
 */
export default function FloatingAiAssistantButton({ draftArtifactVersionId, isSmallScreen, reopenButtonRef, trailingContent }: FloatingAiAssistantButtonProps) {
  const dispatch = useAppDispatch()
  const leaveDraft = useLeaveDraftOnSmallScreen()
  const chatPanelOpen = useAppSelector((state: RootState) => selectChatPanelOpen(state, { draftArtifactVersionId }))

  // Small screens have no docked panel: the button leaves the draft instead, going back to the normal chat.
  if (!isSmallScreen && chatPanelOpen) return null

  // This button always opens the assistant tab, since the source chip beside it (if a source is open) offers a way to open the source tab.
  const openPanelOnAssistantTab = () => {
    dispatch(DraftWorkspaceActions.setChatPanelTab({ draftArtifactVersionId, tab: ChatPanelTabType.Assistant }))
    dispatch(DraftWorkspaceActions.setChatPanelOpen({ draftArtifactVersionId, open: true }))
  }

  return (
    <FloatingAssistantButton
      isSmallScreen={isSmallScreen}
      onClick={isSmallScreen ? leaveDraft : openPanelOnAssistantTab}
      buttonRef={reopenButtonRef}
      trailingContent={trailingContent}
    />
  )
}
